import { DetailSections } from '../components/ui/DetailSections';
import { detailSections } from '../config/detailSections';
import { useDetailSection } from '../hooks/useDetailSection';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  ArrowLeft,
  Loader,
  AlertCircle,
  UserCheck,
  MessageSquare,
  FileArchive,
  Server,
  Package,
  Calendar,
  Ticket,
  GitBranch,
  Activity,
  Images,
  Pencil,
  Trash2,
  Copy,
  Check,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Hammer,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';
import { buildTeamApiService } from '../services/buildTeamApi';
import { usernameCacheService } from '../services/usernameCache';
import {
  BuildProjectFull,
  BuildProjectStatus,
  BuildTag,
  BUILD_PROJECT_STATUSES,
} from '../types/buildTeam';
import { BuildStatusBadge } from '../components/buildteam/BuildStatusBadge';
import { BuildPriorityBadge } from '../components/buildteam/BuildPriorityBadge';
import { BuildAssignmentsSection } from '../components/buildteam/BuildAssignmentsSection';
import { BuildCommentsSection } from '../components/buildteam/BuildCommentsSection';
import { BuildSchematicsSection } from '../components/buildteam/BuildSchematicsSection';
import { BuildProjectGallerySection } from '../components/buildteam/BuildProjectGallerySection';
import { BuildServersSection } from '../components/buildteam/BuildServersSection';
import { BuildModpacksSection } from '../components/buildteam/BuildModpacksSection';
import { BuildCalendarSection } from '../components/buildteam/BuildCalendarSection';
import { BuildTicketsSection } from '../components/buildteam/BuildTicketsSection';
import { BuildProjectLinksSection } from '../components/buildteam/BuildProjectLinksSection';
import { BuildActivityLog } from '../components/buildteam/BuildActivityLog';
import { ApogeeLinkedPackSummary } from '../components/apogee/ApogeeLinkedPackSummary';
import { EditProjectModal } from '../components/buildteam/EditProjectModal';
import { CreateBuildItemModal } from '../components/buildteam/CreateBuildItemModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { CreatorProfile } from '../components/CreatorProfile';
import { ApogeePackEnrichment } from '../components/apogee/ApogeePackEnrichment';
import { format, formatDistanceToNow, isPast, differenceInDays } from 'date-fns';
import { KanbanLinkedCardsSection } from '../components/kanban/KanbanLinkedCardsSection';
import { PageHeader } from '../components/ui/PageHeader';
import { PanelTour } from '../components/ui/PanelTour';
import { buildWorkTour } from '../config/panelTours';
import { useTourNavigation } from '../hooks/useTourNavigation';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

type TabId = 'overview'
  | 'builds'
  | 'assignments'
  | 'comments'
  | 'schematics'
  | 'gallery'
  | 'servers'
  | 'modpacks'
  | 'calendar'
  | 'tickets'
  | 'links'
  | 'activity';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'overview', label: 'Brief & dates', icon: BookOpen },
  { id: 'builds', label: 'Builds', icon: Hammer },
  { id: 'assignments', label: 'Assignments', icon: UserCheck },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'schematics', label: 'Schematics', icon: FileArchive },
  { id: 'gallery', label: 'Gallery', icon: Images },
  { id: 'servers', label: 'Servers', icon: Server },
  { id: 'modpacks', label: 'Modpacks', icon: Package },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'tickets', label: 'Tickets', icon: Ticket },
  { id: 'links', label: 'Links', icon: GitBranch },
  { id: 'activity', label: 'Activity', icon: Activity },
];

function getDueDateStyle(dueDateUnix: number, status?: string): { className: string; label: string } {
  // Completed/cancelled work is never "overdue" or "due soon" — show the date neutrally.
  if (status === 'completed' || status === 'cancelled') {
    return { className: 'text-glass-400', label: '' };
  }
  const dueDate = new Date(dueDateUnix * 1000);
  if (isPast(dueDate)) {
    return { className: 'text-red-400', label: 'Overdue' };
  }
  const daysLeft = differenceInDays(dueDate, new Date());
  if (daysLeft <= 3) {
    return { className: 'text-amber-400', label: 'Due soon' };
  }
  return { className: 'text-glass-400', label: '' };
}

export const BuildProjectDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isMobile = useIsMobile();

  const isDev = import.meta.env.DEV;

  const [project, setProject] = useState<BuildProjectFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useDetailSection(TABS, 'builds');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [assignRequest, setAssignRequest] = useState(0);
  const { tourNavigation } = useTourNavigation();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreateBuildOpen, setIsCreateBuildOpen] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // Tag management
  const [allTags, setAllTags] = useState<BuildTag[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagActionLoading, setTagActionLoading] = useState<string | null>(null);

  // Priority quick-change
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [priorityChanging, setPriorityChanging] = useState(false);

  const canEdit = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return (
      groups.includes('argus_build_panel') ||
      groups.includes('argus_build_lead') ||
      groups.includes('argus_build_admin') ||
      groups.includes('SPY_Super_admin')
    );
  }, [identity, isDev]);

  const canCreate = canEdit;

  const canDelete = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_build_admin') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canLead = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return (
      groups.includes('argus_build_lead') ||
      groups.includes('argus_build_admin') ||
      groups.includes('SPY_Super_admin')
    );
  }, [identity, isDev]);

  useDocumentTitle(project ? `Project: ${project.title}` : 'Project Details');

  const loadProject = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await buildTeamApiService.getProjectById(id);
      setProject(data);

      const uuids = new Set<string>();
      if (data.created_by) uuids.add(data.created_by);
      if (data.updated_by) uuids.add(data.updated_by);
      data.latest_activity?.forEach((a) => uuids.add(a.actor_id));
      if (uuids.size > 0) {
        usernameCacheService.prefetchUsernames(Array.from(uuids));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useEffect(() => {
    buildTeamApiService.listTags().then(setAllTags).catch((err) => {
      console.error('Failed to load tags:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    });
  }, []);

  useEffect(() => {
    const handleClick = () => {
      setShowTagPicker(false);
      setShowPriorityDropdown(false);
      setShowStatusDropdown(false);
    };
    if (showTagPicker || showPriorityDropdown || showStatusDropdown) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClick, { once: true });
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClick);
      };
    }
  }, [showTagPicker, showPriorityDropdown, showStatusDropdown]);

  const handleAddTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await buildTeamApiService.tagProject(id, tagId);
      await loadProject();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await buildTeamApiService.untagProject(id, tagId);
      await loadProject();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handlePriorityChange = async (newPriority: number) => {
    if (!id || !project) return;
    setPriorityChanging(true);
    setShowPriorityDropdown(false);
    try {
      await buildTeamApiService.updateProject(id, { priority: newPriority });
      await loadProject();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change priority');
    } finally {
      setPriorityChanging(false);
    }
  };

  const handleStatusChange = async (newStatus: BuildProjectStatus) => {
    if (!project || !id) return;
    setStatusChanging(true);
    setShowStatusDropdown(false);
    try {
      let reason: string | undefined;
      if (newStatus === 'cancelled') {
        reason = prompt('Enter cancellation reason:') || undefined;
        if (reason === undefined) {
          setStatusChanging(false);
          return;
        }
      }
      await buildTeamApiService.changeProjectStatus(id, newStatus, reason);
      await loadProject();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change status');
    } finally {
      setStatusChanging(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await buildTeamApiService.deleteProject(id);
      navigate('/build-team');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  const handleCopyId = () => {
    if (!id) return;
    navigator.clipboard.writeText(id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '—';
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return format(new Date(ms), 'MMM d, yyyy');
  };

  const formatCategoryLabel = (cat: string) => {
    return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        {/* Header skeleton */}
        <div className="space-y-3">
          <SkeletonLoader className="h-3 w-24" />
          <div className="flex items-center gap-3">
            <SkeletonLoader className="h-7 w-64 max-w-full" />
            <SkeletonLoader className="h-5 w-20 rounded-full" />
            <SkeletonLoader className="h-5 w-10 rounded-full" />
          </div>
          <SkeletonLoader className="h-4 w-80 max-w-full" />
        </div>
        {/* Info cards */}
        <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
          {[0, 1].map((i) => (
            <div key={i} className="glass-card p-4 md:p-5 space-y-3">
              <SkeletonLoader className="h-4 w-28" />
              <SkeletonLoader className="h-3 w-full" />
              <SkeletonLoader className="h-3 w-5/6" />
              <SkeletonLoader className="h-3 w-2/3" />
            </div>
          ))}
        </div>
        {/* Tabs */}
        <div className="glass-card">
          <div className="flex gap-4 px-4 py-3 border-b border-glass-200/10">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-4 w-16" />
            ))}
          </div>
          <div className="p-4 md:p-5 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-14 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Build Team"
          title="Project"
          backTo="/build-team"
          backLabel="Build Team"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load project"
          description={error || 'Project not found'}
          action={
            <>
              <button onClick={loadProject} className="glass-button">Retry</button>
              <button
                onClick={() => navigate('/build-team')}
                className="glass-button-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Build Team
              </button>
            </>
          }
        />
      </div>
    );
  }

  const descriptionTruncated = project.description && project.description.length > 200;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        className="relative z-10"
        eyebrow="Build Team"
        backTo="/build-team"
        backLabel="Build Team"
        title={project.title}
        titleAdornment={
          <>
            {/* Status */}
            <div className="relative flex items-center gap-1">
              <BuildStatusBadge status={project.status} type="project" size="md" />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={statusChanging}
                  className="btn-ghost btn-xs"
                  title="Change status"
                  aria-label="Change status"
                  aria-expanded={showStatusDropdown}
                >
                  {statusChanging ? (
                    <Loader className="w-3 h-3 animate-spin" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                  Change
                </button>
              )}
              {showStatusDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[180px] py-1">
                  {BUILD_PROJECT_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleStatusChange(s)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm hover:bg-glass-200/10 transition-colors',
                        s === project.status
                          ? 'text-accent-300 bg-accent-500/10'
                          : 'text-glass-200'
                      )}
                    >
                      {s
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Priority */}
            <div className="relative flex items-center gap-1">
              <BuildPriorityBadge priority={project.priority} size="md" />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                  disabled={priorityChanging}
                  className="btn-icon btn-icon-sm"
                  title="Change priority"
                  aria-label="Change priority"
                  aria-expanded={showPriorityDropdown}
                >
                  {priorityChanging ? (
                    <Loader className="w-3 h-3 animate-spin" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </button>
              )}
              {showPriorityDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[120px] max-h-[300px] overflow-y-auto py-1">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handlePriorityChange(p)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm tabular-nums hover:bg-glass-200/10 transition-colors',
                        p === project.priority
                          ? 'text-accent-300 bg-accent-500/10'
                          : 'text-glass-200'
                      )}
                    >
                      Priority {p}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {project.category && (
              <span className="badge bg-indigo-500/15 text-indigo-300 border-indigo-500/25">
                {formatCategoryLabel(project.category)}
              </span>
            )}
          </>
        }
        description={
          project.description ? (
            <>
              <span className="whitespace-pre-wrap">
                {descriptionExpanded || !descriptionTruncated
                  ? project.description
                  : `${project.description.slice(0, 200)}...`}
              </span>
              {descriptionTruncated && (
                <button
                  type="button"
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  className="ml-2 text-xs text-accent-300 hover:text-accent-200 inline-flex items-center gap-0.5 align-baseline transition-colors"
                >
                  {descriptionExpanded ? (
                    <>
                      <ChevronUp className="w-3 h-3" /> Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3" /> Show more
                    </>
                  )}
                </button>
              )}
            </>
          ) : undefined
        }
        actions={
          <>
            <button
              onClick={handleCopyId}
              className="glass-button-secondary"
              title="Copy ID"
              aria-label="Copy project ID"
            >
              {copiedId ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              {copiedId ? 'Copied' : 'ID'}
            </button>
            <PanelTour
              tourId="build-work"
              title="Build project"
              steps={buildWorkTour('project', canLead, canCreate, true)}
              enabled={!!project}
              {...tourNavigation}
            />
            {canLead && (
              <button
                type="button"
                data-tour="build-assign-quick"
                onClick={() => {
                  setActiveTab('assignments');
                  setAssignRequest((n) => n + 1);
                }}
                className="glass-button"
                title="Assign a builder to this project"
              >
                <UserPlus className="w-4 h-4" />
                Assign builder
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => setIsEditOpen(true)}
                className="glass-button"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => setIsDeleteOpen(true)}
                className="btn-danger"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </>
        }
      >
        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="eyebrow mr-0.5">Tags</span>
          {project.tags?.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border group/tag"
              style={{
                backgroundColor: `${tag.color}20`,
                borderColor: `${tag.color}40`,
                color: tag.color,
              }}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: tag.color }}
              />
              {tag.name}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag.id)}
                  disabled={tagActionLoading === tag.id}
                  className={cn(
                    'ml-0.5 transition-opacity hover:brightness-125 touch-manipulation p-0.5',
                    isMobile ? 'opacity-100' : 'opacity-0 group-hover/tag:opacity-100 focus-visible:opacity-100'
                  )}
                  title="Remove tag"
                  aria-label={`Remove tag ${tag.name}`}
                >
                  {tagActionLoading === tag.id ? (
                    <Loader className="w-3 h-3 animate-spin" />
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                </button>
              )}
            </span>
          ))}
          {!project.tags?.length && !canEdit && (
            <span className="text-xs text-glass-500">None</span>
          )}
          {canEdit && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTagPicker(!showTagPicker)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-dashed border-glass-200/25 text-glass-400 hover:text-white hover:border-glass-200/40 transition-colors touch-manipulation"
                aria-expanded={showTagPicker}
              >
                <Plus className="w-3 h-3" />
                Tag
              </button>
              {showTagPicker && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-20 min-w-[180px] max-h-[200px] overflow-y-auto py-1">
                  {(() => {
                    const currentTagIds = new Set(
                      project.tags?.map((t) => t.id) || []
                    );
                    const available = allTags.filter(
                      (t) => !currentTagIds.has(t.id)
                    );
                    if (available.length === 0) {
                      return (
                        <div className="px-3 py-2 text-xs text-glass-500">
                          No more tags available
                        </div>
                      );
                    }
                    return available.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          handleAddTag(tag.id);
                          setShowTagPicker(false);
                        }}
                        disabled={tagActionLoading === tag.id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-glass-200/10 transition-colors flex items-center gap-2"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="text-glass-200">{tag.name}</span>
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </PageHeader>

      {/* Tabs */}
      <DetailSections sections={detailSections('buildProject', TABS, { assignments: project.assignments?.length, comments: project.comments_count, schematics: project.schematics_count, gallery: project.images_count, servers: project.server_links?.length, modpacks: project.modpack_links?.length, builds: project.builds?.length })} active={activeTab} onChange={setActiveTab} navTour="build-work-sections">
        {activeTab === 'overview' && (<>      {/* Info Cards */}
      <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
        {/* Quick Stats */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Quick Stats" icon={Activity} description="Open a section from its count." />
          <ul className="space-y-1 text-sm">
            {(
              [
                ['builds', 'Builds', project.builds?.length ?? 0],
                ['assignments', 'Assignments', project.assignments?.length ?? 0],
                ['schematics', 'Schematics & screenshots', project.schematics_count ?? 0],
                ['gallery', 'Pictures', project.images_count ?? 0],
                ['comments', 'Comments', project.comments_count ?? 0],
              ] as [TabId, string, number][]
            ).map(([section, label, count]) => (
              <li key={section}>
                <button
                  type="button"
                  onClick={() => setActiveTab(section)}
                  className="w-full flex justify-between gap-3 px-2 py-1 -mx-2 rounded-lg text-left text-glass-400 hover:bg-glass-200/10 hover:text-white transition-colors"
                >
                  <span>{label}</span>
                  <span className="text-glass-200 font-medium tabular-nums">{count}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Dates & Metadata */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Key Dates & Metadata" icon={Calendar} />
          <dl className="space-y-2 text-sm">
            {(
              [
                ['Due Date', project.due_date],
                ['Started At', project.started_at],
                ['Completed At', project.completed_at],
              ] as [string, number | null][]
            ).map(([label, val]) => {
              const dueDateStyle = label === 'Due Date' && val ? getDueDateStyle(val, project.status) : null;
              return (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="text-glass-400">{label}</dt>
                  <dd className={cn('tabular-nums', dueDateStyle ? dueDateStyle.className : 'text-glass-200')}>
                    {formatDate(val)}
                    {dueDateStyle?.label && (
                      <span className="ml-1.5 text-xs">({dueDateStyle.label})</span>
                    )}
                  </dd>
                </div>
              );
            })}
            {project.category && (
              <div className="flex justify-between gap-3">
                <dt className="text-glass-400">Category</dt>
                <dd className="text-glass-200">
                  {formatCategoryLabel(project.category)}
                </dd>
              </div>
            )}
            {project.apogee_id != null && (
              <div className="flex flex-col gap-2 pt-1">
                <dt className="text-glass-400">Primary modpack (Apogee)</dt>
                <dd>
                  <ApogeePackEnrichment apogeeId={project.apogee_id} mode="inline" />
                </dd>
              </div>
            )}
          </dl>
          {project.apogee_id != null && (
            <div className="mt-4">
              <ApogeeLinkedPackSummary apogeeId={project.apogee_id} variant="featured" />
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-glass-200/10 space-y-1 text-xs text-glass-500">
            <div className="flex justify-between gap-3">
              <span>Created</span>
              <span className="tabular-nums text-right">
                {formatDate(project.created_at)} by{' '}
                <CreatorProfile creatorId={project.created_by} inline />
              </span>
            </div>
            {project.updated_at && (
              <div className="flex justify-between gap-3">
                <span>Updated</span>
                <span className="tabular-nums text-right">
                  {formatDistanceToNow(
                    new Date(
                      project.updated_at < 1e12
                        ? project.updated_at * 1000
                        : project.updated_at
                    ),
                    { addSuffix: true }
                  )}
                  {project.updated_by && (
                    <>
                      {' '}
                      by <CreatorProfile creatorId={project.updated_by} inline />
                    </>
                  )}
                </span>
              </div>
            )}
          </div>

          {project.metadata && (
            <div className="mt-3 pt-3 border-t border-glass-200/10">
              <div className="eyebrow mb-1.5">Metadata</div>
              <pre className="subcard p-3 text-xs font-mono text-glass-300 overflow-x-auto">
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(project.metadata), null, 2);
                  } catch {
                    return project.metadata;
                  }
                })()}
              </pre>
            </div>
          )}
        </div>
      </div>

</>)}
        {activeTab === 'builds' && (<>      {/* Builds List */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Builds"
          icon={Hammer}
          count={project.builds?.length ?? 0}
          actions={
            canCreate && (
              <button
                onClick={() => setIsCreateBuildOpen(true)}
                className="glass-button glass-button-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Build
              </button>
            )
          }
        />
        {project.builds && project.builds.length > 0 ? (
          <AnimatePresence>
            <div className="divide-y divide-glass-200/10">
              {project.builds.map((build, index) => (
                <motion.div
                  key={build.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15, delay: index * 0.03 }}
                  className="flex items-center gap-3 py-2.5 group"
                >
                  <button
                    onClick={() => navigate(`/build-team/builds/${build.id}`)}
                    className="flex-1 min-w-0 text-left text-sm text-white hover:text-accent-300 font-medium truncate transition-colors"
                  >
                    {build.title}
                  </button>
                  {build.due_date && (() => {
                    const style = getDueDateStyle(build.due_date, build.status);
                    return style.label ? (
                      <span className={cn('text-[11px] font-medium', style.className)}>{style.label}</span>
                    ) : null;
                  })()}
                  <BuildStatusBadge status={build.status} type="build" size="sm" />
                  <BuildPriorityBadge priority={build.priority} size="sm" />
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        ) : (
          <EmptyState
            bare
            compact
            icon={Hammer}
            title="No builds yet"
            description="No builds associated with this project yet."
          />
        )}
      </div>

</>)}

          {activeTab === 'assignments' && (
            <BuildAssignmentsSection
              targetId={project.id}
              targetType="project"
              canLead={canLead}
              onUpdate={loadProject}
              openAddFormSignal={assignRequest}
            />
          )}
          {activeTab === 'comments' && (
            <BuildCommentsSection
              targetId={project.id}
              targetType="project"
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadProject}
            />
          )}
          {activeTab === 'schematics' && (
            <BuildSchematicsSection
              targetId={project.id}
              targetType="project"
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadProject}
            />
          )}
          {activeTab === 'gallery' && (
            <BuildProjectGallerySection
              projectId={project.id}
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadProject}
            />
          )}
          {activeTab === 'servers' && (
            <BuildServersSection
              targetId={project.id}
              targetType="project"
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadProject}
            />
          )}
          {activeTab === 'modpacks' && (
            <BuildModpacksSection
              targetId={project.id}
              targetType="project"
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadProject}
            />
          )}
          {activeTab === 'calendar' && (
            <BuildCalendarSection
              targetId={project.id}
              targetType="project"
              canEdit={canEdit}
              onUpdate={loadProject}
            />
          )}
          {activeTab === 'tickets' && (
            <BuildTicketsSection
              targetId={project.id}
              targetType="project"
              canEdit={canEdit}
              onUpdate={loadProject}
            />
          )}
          {activeTab === 'links' && (
            <BuildProjectLinksSection
              projectId={project.id}
              canEdit={canEdit}
              onUpdate={loadProject}
            />
          )}
          {activeTab === 'activity' && (
            <BuildActivityLog targetId={project.id} targetType="project" />
          )}

      </DetailSections>

      {/* Edit Project Modal */}
      {isEditOpen && (
        <EditProjectModal
          isOpen={isEditOpen}
          project={project}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            loadProject();
          }}
        />
      )}

      {/* Create Build Item Modal */}
      {isCreateBuildOpen && (
        <CreateBuildItemModal
          isOpen={isCreateBuildOpen}
          projectId={project.id}
          onClose={() => setIsCreateBuildOpen(false)}
          onSuccess={() => {
            setIsCreateBuildOpen(false);
            loadProject();
          }}
        />
      )}

      {/* Linked Kanban Cards */}
      <KanbanLinkedCardsSection linkType="build_project" targetId={project.id} title="Linked Kanban Cards" />

      {/* Delete Confirmation */}
      {isDeleteOpen && (
        <ConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleDelete}
          title="Delete Project"
          message="Are you sure you want to delete this project? This will also delete all associated builds, assignments, comments, schematics, server links, modpack links, calendar links, ticket links, and activity log entries. This action cannot be undone."
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
